import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors ' +
    'disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 ' +
    'focus-visible:outline-offset-2 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--accent)] text-[var(--accent-contrast)] hover:opacity-90',
        secondary:
          'surface-raised border text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]',
        ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
        danger: 'bg-danger-500 text-white hover:opacity-90',
      },
      size: {
        // Sized for a thumb, not a mouse pointer: this is the default because most taps
        // happen on a phone at a counter.
        md: 'h-11 px-4 text-sm',
        lg: 'h-14 px-6 text-base',
        sm: 'h-9 px-3 text-sm',
        icon: 'h-11 w-11',
      },
      full: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', full: false },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      // Defaults to "button": an unmarked button inside a form submits it, which in a POS
      // means an accidental Enter can complete a transaction.
      type={type}
      className={cn(button({ variant, size, full }), className)}
      {...props}
    />
  ),
)

Button.displayName = 'Button'
